import * as React from "react";
import { Autocomplete as MUIAutocomplete, TextField, CircularProgress } from "@mui/material";

/**
 * Autocomplete com busca assíncrona (debounced) e paginação por scroll - diferente de
 * Autocomplete.js, que só suporta uma lista estática em memória. Não é ligado ao
 * react-hook-form: o componente pai controla `value`/`onChange` e decide o que fazer
 * com a opção selecionada (ex.: distribuir os dados dela em vários campos do formulário).
 *
 * `fetchOptions(search, page)` deve devolver `{ data, hasMore }` - ao rolar a lista até
 * o fim, a próxima página é buscada e anexada ao final das opções já carregadas.
 */
const AsyncAutocomplete = ({
  label,
  value,
  onChange,
  fetchOptions,
  getOptionLabel = (option) => option?.nome ?? "",
  disabled,
  debounceMs = 400,
  ...props
}) => {
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  // Guarda síncrona além do estado `loadingMore`: vários eventos de scroll podem
  // disparar antes do re-render aplicar o setLoadingMore(true), duplicando o fetch.
  const carregandoMaisRef = React.useRef(false);

  React.useEffect(() => {
    let active = true;
    setLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const result = await fetchOptions(inputValue, 1);
        if (active) {
          setOptions(result?.data || []);
          setHasMore(!!result?.hasMore);
          setPage(1);
        }
      } catch (error) {
        console.error("Erro ao buscar opções:", error);
        if (active) {
          setOptions([]);
          setHasMore(false);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, debounceMs);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
    // `fetchOptions` entra aqui de propósito: quando o pai troca o filtro (ex.: a
    // unidade operacional, que muda a busca de consultor técnico), ele devolve uma
    // função nova e a lista precisa ser recarregada. Sem isso a lista continuava a
    // da unidade anterior até a pessoa digitar alguma coisa. Todo pai memoriza a
    // função com useCallback, senão isso viraria laço de busca.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, fetchOptions]);

  const carregarProximaPagina = React.useCallback(async () => {
    if (loading || carregandoMaisRef.current || !hasMore) return;
    carregandoMaisRef.current = true;
    setLoadingMore(true);
    const termoDaBusca = inputValue;
    try {
      const proximaPagina = page + 1;
      const result = await fetchOptions(termoDaBusca, proximaPagina);
      // Se o termo de busca mudou enquanto essa página carregava, o resultado é de
      // uma busca antiga - descarta em vez de anexar numa lista que já foi trocada.
      if (termoDaBusca !== inputValue) return;
      setOptions((atual) => [...atual, ...(result?.data || [])]);
      setHasMore(!!result?.hasMore);
      setPage(proximaPagina);
    } catch (error) {
      console.error("Erro ao carregar mais opções:", error);
    } finally {
      carregandoMaisRef.current = false;
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore, page, inputValue, fetchOptions]);

  const handleScroll = (event) => {
    const listbox = event.currentTarget;
    const chegouAoFim =
      listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 32;
    if (chegouAoFim) carregarProximaPagina();
  };

  return (
    <MUIAutocomplete
      options={options}
      loading={loading}
      disabled={disabled}
      value={value ?? null}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      noOptionsText="Nenhum resultado"
      filterOptions={(x) => x}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      onChange={(event, newValue) => onChange(newValue)}
      ListboxProps={{ onScroll: handleScroll }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || loadingMore ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...props}
    />
  );
};

export default AsyncAutocomplete;
